import type { PlanResponse } from "../../lib/api";

// PRD PLANS_CATALOG §1 (2026-10-28): a plan is a flat monthly price covering
// `included_cameras`; every camera beyond that bills the overage; the one prepaid term is
// twelve months at the plan row's percentage, packaged in whole dollars rounded down. Pure
// helpers shared by the wizard's plan picker, its prepay copy and the store's Billing tab,
// so the numbers a customer sees are the numbers the backend's `pricing.py` charges.

export type PrepayTerm = 1 | 12;

export const prepayPercent = (plan: PlanResponse, term: PrepayTerm): number =>
  term === 12 ? plan.prepay_12_pct : 0;

export const CONTACT_URL = "https://www.shobdo.ai/#contact";

export const extraCameras = (plan: PlanResponse, cameras: number): number =>
  Math.max(0, cameras - plan.included_cameras);

export const monthlyCents = (plan: PlanResponse, cameras: number): number =>
  plan.monthly_price_cents + plan.overage_cents_per_camera * extraCameras(plan, cameras);

// Mirrors `pricing.compute_charge`: the discount floors to the cent, then a prepaid term
// drops its cents — what the page shows is what the engine charges ($637.20 → $637).
export const termCents = (plan: PlanResponse, cameras: number, term: PrepayTerm): number => {
  const base = monthlyCents(plan, cameras) * term;
  const discounted = base - Math.floor((base * prepayPercent(plan, term)) / 100);
  return term === 1 ? discounted : Math.floor(discounted / 100) * 100;
};

export const termSavingsCents = (plan: PlanResponse, cameras: number, term: PrepayTerm): number =>
  monthlyCents(plan, cameras) * term - termCents(plan, cameras, term);

// Every shipped plan bills the cameras beyond its count; a hard-capped plan
// (`camera_overage` false) cannot be picked by a store already past it.
export const fitsPlan = (plan: PlanResponse, cameras: number): boolean =>
  plan.camera_overage || cameras <= plan.included_cameras;

export const dollars = (cents: number): string => {
  const whole = Math.floor(cents / 100);
  const rest = cents % 100;
  const grouped = whole.toLocaleString("en-US");
  return rest === 0 ? `$${grouped}` : `$${grouped}.${String(rest).padStart(2, "0")}`;
};

export const sellablePlans = (plans: PlanResponse[]): PlanResponse[] =>
  plans.filter((p) => p.sellable);

// The cheapest sellable plan for the store's camera count, overage included (a 4-camera
// store is cheapest on Starter, a 7-camera store on Standard); Standard when the camera
// count is unknown (zero) or nothing fits. Ties go to the plan with more room.
export const recommendedPlan = (plans: PlanResponse[], cameras: number): PlanResponse | null => {
  const offered = sellablePlans(plans);
  if (offered.length === 0) return null;
  const standard = offered.find((p) => p.code === "standard") ?? offered[0] ?? null;
  if (cameras <= 0) return standard;
  const fitting = offered.filter((p) => fitsPlan(p, cameras));
  if (fitting.length === 0) return standard;
  return fitting.reduce((best: PlanResponse, p: PlanResponse) => {
    const cost = monthlyCents(p, cameras);
    const bestCost = monthlyCents(best, cameras);
    if (cost < bestCost) return p;
    if (cost === bestCost && p.included_cameras > best.included_cameras) return p;
    return best;
  });
};

// The smallest sellable plan whose included cameras cover the store outright.
export const coveringPlan = (plans: PlanResponse[], cameras: number): PlanResponse | null =>
  sellablePlans(plans)
    .filter((p) => p.included_cameras >= cameras)
    .sort((a, b) => a.included_cameras - b.included_cameras)[0] ?? null;

export const BADGES: Record<string, string> = {
  percam: "Pay as you go",
  starter: "For small shops",
  standard: "Fits most stores up to 8 cameras",
  pro: "For multi-aisle stores",
};
